import { apiFetch } from './apiClient';
import { sendEmailVerification } from 'firebase/auth';

/**
 * Load Razorpay script
 */
export const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });

/**
 * Detect user country (IP based)
 */
const getUserCountry = async () => {
  try {
    const res = await fetch('https://ipapi.co/json/');
    const data = await res.json();
    return data && data.country_code ? data.country_code : 'US';
  } catch (e) {
    return 'US';
  }
};

/**
 * Dual-currency Razorpay checkout
 */
export const initializeRazorpaySubscription = async (
  planType,
  user,
  onSuccess,
  onError
) => {
  try {
    /* ---------------- EMAIL VERIFICATION ---------------- */
    if (!user.emailVerified) {
      await sendEmailVerification(user);
      throw new Error(
        'Please verify your email before upgrading. We’ve sent you a verification link.'
      );
    }

    const loaded = await loadRazorpayScript();
    if (!loaded) {
      throw new Error('Razorpay SDK failed to load');
    }

    const key = process.env.REACT_APP_RAZORPAY_KEY_ID;
    if (!key) {
      throw new Error('Razorpay key missing');
    }

    /* ---------------- COUNTRY ---------------- */
    const country = await getUserCountry();
    const isIndia = country === 'IN';

    /* ---------------- PLAN CONFIG ---------------- */
    const PLANS = {
      plus: isIndia
        ? {
            amount: 169900, // ₹1,699
            currency: 'INR',
            description: 'NT Studios Plus Plan',
          }
        : {
            amount: 1900, // $19
            currency: 'USD',
            description: 'NT Studios Plus Plan',
          },

      pro: isIndia
        ? {
            amount: 349900, // ₹3,499
            currency: 'INR',
            description: 'NT Studios Pro Plan',
          }
        : {
            amount: 3900, // $39
            currency: 'USD',
            description: 'NT Studios Pro Plan',
          },
    };

    const plan = PLANS[planType];
    if (!plan) {
      throw new Error('Invalid plan');
    }

    /* ---------------- CHECKOUT ---------------- */
    const options = {
      key: key,
      amount: plan.amount,
      currency: plan.currency,
      name: 'NT Studios',
      description: plan.description,

      prefill: {
        name: user.displayName || '',
        email: user.email || '',
        contact: user.phoneNumber || '',
      },

      notes: {
        plan: planType,
        uid: user.uid,
      },

      handler: async (response) => {
        try {
          await apiFetch('/api/subscriptions/confirm', {
            method: 'POST',
            body: JSON.stringify({
              payment_id: response.razorpay_payment_id,
              plan: planType,
            }),
          });

          window.dispatchEvent(new Event('nt:subscription-updated'));

          if (onSuccess) onSuccess();
        } catch (err) {
          if (onError) onError(err);
        }
      },

      modal: {
        ondismiss: () => {
          if (onError) onError(new Error('Payment cancelled by user'));
        },
      },

      theme: {
        color: '#09a6a3',
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  } catch (err) {
    if (onError) onError(err);
  }
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async () => {
  const res = await apiFetch('/api/subscriptions/cancel', {
    method: 'POST',
  });

  window.dispatchEvent(new Event('nt:subscription-updated'));
  return res;
};
